import { z } from "zod";
import type { AutoTuneStateRow } from "@/pages/auto-tune/data/coreAdapters";

// Per-day auto-tune savings point from
// `GET /jobs/{job_id}/auto-tune/savings-trends`. ``dbu_savings`` and
// ``infra_savings`` are the two components of the day's savings; the
// chart stacks them and the tooltip breaks them out.
export const AutoTuneSavingsTrendPointSchema = z.object({
  period: z.string(),
  dbu_savings: z.number().default(0),
  infra_savings: z.number().default(0),
  // The leg this point's savings belong to: the knob on clusters
  // (``resize`` / ``auto_terminate``), the task-run role on jobs
  // (``worker_sizing`` / ``driver_sizing``). ``null`` on a backend that
  // predates the split — the tooltip then shows totals only.
  rec_type: z.string().nullable().optional(),
});

export type AutoTuneSavingsTrendPoint = z.infer<typeof AutoTuneSavingsTrendPointSchema>;

// Mirrors the BE ``AutoTuneSavingsTrendsResponse``. ``actual`` is the
// realized savings on runs that had an applied recommendation in effect;
// ``potential`` is what those same runs would have saved from the active
// recommendation. Each series is bucketed by the run window.
export const AutoTuneSavingsTrendsResponseSchema = z.object({
  actual: z.array(AutoTuneSavingsTrendPointSchema).default([]),
  potential: z.array(AutoTuneSavingsTrendPointSchema).default([]),
  granularity: z.string().default("day"),
});

export type AutoTuneSavingsTrendsResponse = z.infer<typeof AutoTuneSavingsTrendsResponseSchema>;

// FE-derived point for the single stacked chart, mirroring the Snowflake
// warehouse auto-manage graph. ``spend``, ``potential`` and ``realized``
// are the day's measured figures; only the last two are stacked directly,
// over ``spendAfterPossibleSavings`` (see below) rather than over the raw
// ``spend``. Realized and potential each carry their DBU/infra split for
// the tooltip. Built client-side by merging the savings
// ``actual``/``potential`` series with the job's per-day cost on ``period``.
// Mirrors the BE ``AutoTuneHistoryEvent`` Pydantic schema produced by
// `GET /jobs/{job_id}/auto-tune/history`. The BE composes
// ``event_message`` server-side via ``DBX_AUTO_TUNE_EVENT_UI_MAPPING``
// so the FE renders one string verbatim and only needs to pick the
// icon based on ``event_type`` × ``mode``.
// The enum spans every real BE ``event_type`` across jobs and cluster
// Auto Tune (jobs emits a subset). Listing them all keeps each one a
// valid history filter value — e.g. ``BACKOFF`` — instead of a value the
// UI silently drops.
export const AutoTuneHistoryEventTypeSchema = z.enum([
  "APPLY_REQUESTED",
  "MUTATION_STARTED",
  "APPLIED",
  "BACKOFF",
  "REVERTED",
  "FAILED",
  "CANCELLED",
  "JOB_AUTO_TUNE_ENABLED",
  "JOB_AUTO_TUNE_DISABLED",
  "CONFIG_CHANGED",
]);

// Human-readable labels for each customer-relevant history ``event_type``,
// in display order, driving the history event-type filter. Internal
// states (``MUTATION_STARTED``, ``CANCELLED``) are intentionally excluded
// here so the filter combobox can never let a user select them — the
// underlying enum above still accepts every BE value so a mid-rollout
// response or the BE escape hatch never fails to parse.
export const AUTO_TUNE_HISTORY_EVENT_TYPE_OPTIONS: {
  value: z.infer<typeof AutoTuneHistoryEventTypeSchema>;
  label: string;
}[] = [
  { value: "APPLY_REQUESTED", label: "Enable requested" },
  { value: "APPLIED", label: "Applied" },
  { value: "BACKOFF", label: "Auto rolled back" },
  { value: "REVERTED", label: "Disabled" },
  { value: "FAILED", label: "Failed" },
  // Backoff/notification setting edits, worded like the warehouse
  // history's "Threshold changes" group.
  { value: "CONFIG_CHANGED", label: "Threshold changes" },
];

// The customer-visible ``event_type`` values, derived from the option
// list above. The jobs and cluster history endpoints default server-side
// to every one of them except ``CONFIG_CHANGED``, so both panels send this
// list explicitly on an empty filter selection — the request states its
// own filter and the combobox semantics stay pinned client-side.
export const AUTO_TUNE_HISTORY_CUSTOMER_VISIBLE_EVENT_TYPES: string[] =
  AUTO_TUNE_HISTORY_EVENT_TYPE_OPTIONS.map(option => option.value);

// Job history filter options: the job-level enable/disable audit rows
// (one per click) sit first, and ``REVERTED`` reads "Rolled back" since a
// user disable is now its own "Disabled" entry. Jobs only; clusters keep
// ``AUTO_TUNE_HISTORY_EVENT_TYPE_OPTIONS``.
export const JOB_AUTO_TUNE_HISTORY_EVENT_TYPE_OPTIONS: {
  value: z.infer<typeof AutoTuneHistoryEventTypeSchema>;
  label: string;
}[] = [
  { value: "JOB_AUTO_TUNE_ENABLED", label: "Enabled" },
  { value: "JOB_AUTO_TUNE_DISABLED", label: "Disabled" },
  { value: "APPLY_REQUESTED", label: "Enable requested" },
  { value: "APPLIED", label: "Applied" },
  { value: "BACKOFF", label: "Auto rolled back" },
  { value: "REVERTED", label: "Rolled back" },
  { value: "FAILED", label: "Failed" },
  { value: "CONFIG_CHANGED", label: "Threshold changes" },
];

export const JOB_AUTO_TUNE_HISTORY_CUSTOMER_VISIBLE_EVENT_TYPES: string[] =
  JOB_AUTO_TUNE_HISTORY_EVENT_TYPE_OPTIONS.map(option => option.value);

// The Agent Decisions chart counts what Auto Tune did, so it leaves out
// threshold edits, which are the user's own setting changes.
export const AUTO_TUNE_DECISION_EVENT_TYPES: string[] =
  AUTO_TUNE_HISTORY_CUSTOMER_VISIBLE_EVENT_TYPES.filter(
    eventType => eventType !== "CONFIG_CHANGED"
  );

export const AutoTuneHistoryEventSchema = z.object({
  event_id: z.number(),
  // Known values for typing; any other string the server sends still
  // parses so one unfamiliar row never fails the whole history page.
  event_type: AutoTuneHistoryEventTypeSchema.or(z.string()),
  mode: z.string().nullable().optional(),
  change_timestamp: z.string(),
  task_key: z.string().nullable().optional(),
  recommendation_from: z.string().nullable().optional(),
  recommendation_to: z.string().nullable().optional(),
  // Per-leg from/to snapshots (jobs compose worker counts and driver/
  // worker instance types as separate legs; the cluster path returns
  // them null). Not rendered anywhere yet — declared so responses parse
  // without unknown-field passthrough warnings.
  // Cluster path: one-line "before → after" for the value an agent
  // decision changed ("5–6 → 4–6 workers", "83 → 45 min"); absent for
  // toggle rows and rows without both snapshots.
  change_summary: z.string().nullable().optional(),
  recommendation_from_workers: z.string().nullable().optional(),
  recommendation_to_workers: z.string().nullable().optional(),
  recommendation_from_worker_instance: z.string().nullable().optional(),
  recommendation_to_worker_instance: z.string().nullable().optional(),
  recommendation_from_driver_instance: z.string().nullable().optional(),
  recommendation_to_driver_instance: z.string().nullable().optional(),
  worker_rec_rk: z.string().nullish(),
  driver_rec_rk: z.string().nullish(),
  changed_by: z.number().nullable().optional(),
  changed_by_name: z.string().nullable().optional(),
  actionable_message: z.string().nullable().optional(),
  reason: z.string().nullable().optional(),
  // Server-composed expandable detail (BACKOFF measurements, FAILED
  // message, or rec reasoning for apply/revert events). Nullish so the
  // all-purpose-cluster surface, which shares this schema but doesn't
  // populate the field, still parses cleanly.
  reason_detail: z.string().nullable().optional(),
  // Instants that ``reason_detail`` / ``reason`` reference by ``{token}``
  // (``{enabled_at}`` and so on). The BE composes the sentence; the FE fills
  // each token in the viewer's zone. Nullish: only rows whose text names an
  // instant carry one, and older backends send none.
  enabled_at: z.string().nullable().optional(),
  rec_generated_at: z.string().nullable().optional(),
  queued_at: z.string().nullable().optional(),
  superseded_applied_at: z.string().nullable().optional(),
  event_message: z.string(),
  // Cluster path only: the knob this history row belongs to (``resize`` /
  // ``auto_terminate``). Drives the Agent Decisions per-knob bar
  // attribution and the history row's knob dot. Jobs callers omit it.
  rec_type: z.string().nullable().optional(),
});

export const AutoTuneHistoryResponseSchema = z.object({
  items: z.array(AutoTuneHistoryEventSchema),
  // Pagination shape mirrors the BE Pydantic model
  // (``total_count`` / ``page`` / ``page_size`` / ``total_pages``).
  // Older responses used ``total`` / ``size`` / ``pages``; aliases
  // remain for back-compat against the storybook fixtures.
  total_count: z.number().default(0),
  page: z.number().default(1),
  page_size: z.number().default(10),
  total_pages: z.number().default(1),
  total: z.number().default(0).optional(),
  size: z.number().default(10).optional(),
  pages: z.number().default(1).optional(),
});

export const AutoTuneStatusPreflightSchema = z.object({
  blocked: z.boolean().default(false),
  reason: z.string().nullable().optional(),
  actionable_message: z.string().nullable().optional(),
  can_manage_blocked: z.boolean().optional(),
  // The entity no longer exists in Databricks but Auto Tune is still armed:
  // enabling stays blocked while turning Auto Tune off is allowed.
  turn_off_allowed: z.boolean().optional(),
});

export const AutoTuneStatusResponseSchema = z.object({
  auto_tune_mode: z.string().nullable().optional(),
  state: z
    .enum(["enabled_active", "enabled_monitoring", "disabled", "unavailable"])
    .nullable()
    .optional(),
  has_applied: z.boolean().nullable().optional(),
  preflight: AutoTuneStatusPreflightSchema.default({ blocked: false }),
  recommendation_count: z.number().default(0),
  tasks_enabled: z.number().default(0),
  tasks_total: z.number().default(0),
  at_scoped_spend_usd: z.number().nullable().optional(),
  // Cluster path only: per-axis sub-toggle flags from the config row
  // (keys ``resize_enabled`` / ``auto_terminate_enabled``). The drawer
  // baselines its switches from these — an armed axis with no active
  // recommendation has no rec item to carry an ``enabled`` annotation.
  // Absent on the jobs path and on backends that predate the field.
  axis_flags: z.record(z.boolean()).nullable().optional(),
  // Cluster path only: per-axis enable gates, keyed by the axis's
  // recommendation ``rec_type``. Each row's switch consumes only its own
  // entry's ``blocked`` boolean — the axis-specific reason/actionable
  // message are for the backend's own logging, not rendered here.
  // Absent on the jobs/warehouse paths and on backends that predate the
  // field.
  axis_preflight: z.record(AutoTuneStatusPreflightSchema).nullable().optional(),
  // Cluster path only: the 3 Auto Tune tab KPI values. Computed
  // server-side so SQL warehouses/job clusters inherit the same
  // aggregate once they reuse this endpoint family. Absent on the jobs
  // path and on backends that predate the fields.
  annualized_cost_savings_usd: z.number().nullable().optional(),
  // All-time realized (actual) DBU savings for the entity — the KPI card's
  // accumulated "Realized savings" figure. Absent on entities that don't
  // compute it.
  realized_savings_usd: z.number().nullable().optional(),
  additional_potential_savings_usd_per_month: z.number().nullable().optional(),
  infra_cost_available: z.boolean().nullable().optional(),
  // Job path only: the canonical enabled-task-key set (durable state
  // rows intersected with the current DAG inventory), computed once and
  // threaded into every surface that needs a per-task enabled flag (the
  // DAG cards) so none of them re-derive it independently. Never present
  // on the wire — constructed client-side, so it is never parsed, only
  // typed. Absent on the cluster/warehouse paths and under v1.
  enabled_task_keys: z.custom<Set<string>>().optional(),
  // Job path only, client-derived like ``enabled_task_keys``: tasks whose
  // state row is still enabled but that are no longer in the DAG
  // inventory. They count toward ``auto_tune_mode`` but not toward any
  // task count, and the settings drawer must still be able to turn them off.
  enabled_task_keys_missing_from_job: z.custom<Set<string>>().optional(),
  // Client-derived job scope, available from stored state before recommendations load.
  is_job_level: z.boolean().optional(),
  // Client-derived, validated stored task/role state shared with the drawer.
  task_states: z.custom<AutoTuneStateRow[]>().optional(),
});

export type AutoTuneStatusResponse = z.infer<typeof AutoTuneStatusResponseSchema>;

export const AutoTuneSettingsScopeSchema = z.enum(["all_tasks", "specific_tasks"]);

export const AutoTuneSettingsSchema = z.object({
  enabled: z.boolean().default(false),
  scope: AutoTuneSettingsScopeSchema.default("all_tasks"),
  enabled_task_keys: z.array(z.string()).default([]),
  reasoning: z.string().nullable().optional(),
});

export type AutoTuneHistoryEventType = z.infer<typeof AutoTuneHistoryEventTypeSchema>;
export type AutoTuneHistoryEvent = z.infer<typeof AutoTuneHistoryEventSchema>;
export type AutoTuneHistoryResponse = z.infer<typeof AutoTuneHistoryResponseSchema>;
export type AutoTuneSettingsScope = z.infer<typeof AutoTuneSettingsScopeSchema>;
export type AutoTuneSettings = z.infer<typeof AutoTuneSettingsSchema>;
